import { ArrowDown } from "lucide-react";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { scrollToId } from "@/components/site/SmoothScroll";
import { LeafCutout } from "@/components/kit/Decor";
import { AboutFeatures, Philosophy, ValuesBand } from "@/components/blocks/AboutBlocks";
import { FounderStory } from "@/components/blocks/FounderStory";
import { CtaBand } from "@/components/kit/CtaBand";
import { JsonLd, personSchema, usePageMeta } from "@/components/site/Seo";
import { SITE } from "@/lib/content";

function AboutHero() {
  return (
    <section data-testid="about-hero" className="relative overflow-hidden bg-beige pb-20 pt-14 sm:pb-28 sm:pt-20">
      <LeafCutout variant="a" className="-right-16 -top-6 z-0 h-40 w-32 opacity-50 sm:-top-12 sm:h-96 sm:w-80 sm:opacity-100" speed={0.2} />
      <div className="wrap relative z-10 grid items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal y={16}><p className="eyebrow"><span className="h-px w-8 bg-gold" />About</p></Reveal>
          <h1 className="mt-6 text-[3rem] leading-[0.95] text-forest sm:text-7xl lg:text-[5.4rem]">
            <MaskLines onLoad delay={0.1} lines={["A Healthier You,", <em key="b" className="text-gold-ink">A Brighter Tomorrow</em>]} />
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-charcoal sm:text-lg">
              Hi, I'm {SITE.founder}, a wellness coach passionate about helping people build healthier, happier lives through real food, simple habits and consistent guidance.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-charcoal-soft sm:text-lg">
              I'm not a dietitian, but a coach who walks with you on your journey, with practical support, accountability and care.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <button onClick={() => scrollToId("my-story")} className="btn-ghost" data-testid="about-my-story-button">My story <ArrowDown className="h-4 w-4" /></button>
              <p className="leading-none" data-testid="about-signature">
                <span className="block font-script text-4xl text-forest">{SITE.founder}</span>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal-soft">Wellness Coach</span>
              </p>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.25} className="lg:col-span-5">
          <div className="relative mx-auto max-w-[420px]">
            <div className="absolute -inset-3 rounded-[999px_999px_34px_34px] border border-gold/50" />
            <div className="arch relative aspect-[4/5] overflow-hidden bg-forest">
              <img src="/images/sony-portrait.jpg" alt={`${SITE.founder}, wellness coach`} className="h-full w-full object-cover object-top" data-testid="about-sony-photo" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  usePageMeta("About Sony Mathew, Wellness Coach", `Sony Mathew is a wellness coach, not a dietitian, who lost ${SITE.founderKg} kg on his own journey.`);
  return (
    <div data-testid="about-page">
      <JsonLd id="person" data={personSchema()} />
      <AboutHero />
      <section className="bg-beige pb-24 sm:pb-28">
        <div className="wrap grid gap-5 lg:grid-cols-12">
          <Reveal className="lg:col-span-5"><AboutFeatures /></Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="grid h-full gap-5">
              <Philosophy />
              <div className="relative min-h-[340px] overflow-hidden rounded-[28px]">
                <img src="/images/sony-full.jpg" alt={`${SITE.founder} standing in a modern office`} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-[50%_8%]" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <div id="my-story"><FounderStory showLink={false} /></div>
      <ValuesBand />
      <CtaBand testId="about-cta" />
    </div>
  );
}
